import React, { useState } from 'react';
import PolicyLayout from './PolicyLayout';
import useDocumentMeta from '../hooks/useDocumentMeta';

const ContactPage = () => {
  useDocumentMeta({
    title: 'Contact — Factory OS',
    description: 'Get in touch with Factory OS — early access, support, privacy, accessibility, security, press, and partnerships.',
    path: '/contact',
  });

  return (
    <PolicyLayout
      title="Contact"
      lead="Pick the channel that matches what you need. We aim to respond within a few business days."
    >
      <h2>Early access &amp; product questions</h2>
      <p>
        Factory OS is in early development. To request access, ask product questions, or share feedback, use the <strong>Request access</strong> button in the page header. We read every submission.
      </p>
      <p>
        You can also email <a href="mailto:hello@factory-os.com">hello@factory-os.com</a>.
      </p>

      <h2>Enterprise &amp; sales</h2>
      <p>
        Evaluating Factory OS for a larger organization — SSO, security reviews, deployment controls, or a walkthrough of your environment? Email <a href="mailto:hello@factory-os.com?subject=Enterprise%20inquiry">hello@factory-os.com</a> with &quot;Enterprise&quot; in the subject and a sentence about your organization, and we&apos;ll set up a call. Include any compliance requirements up front so the right people are in the room. For restricted or export-controlled environments, ask about our ITAR-aligned deployment option — AI inference and embeddings on models you host inside your own boundary.
      </p>

      <h2>Manufacturing inquiries</h2>
      <p>
        For the in-house shop (FDM, CNC, and processes as they come online), submit a part through your design node on the platform once you have access, or email <a href="mailto:shop@factory-os.com">shop@factory-os.com</a> with a brief description and any files you can share. A quote estimate on the platform is non-binding — see the <a href="/terms">Terms of Use</a> for what makes a quote final.
      </p>

      <h2>Privacy requests</h2>
      <p>
        For data-access, correction, export, or deletion requests, or to report a privacy concern, email <a href="mailto:privacy@factory-os.com">privacy@factory-os.com</a>. The full policy is on the <a href="/privacy">Privacy Policy</a> page.
      </p>

      <h2>Accessibility</h2>
      <p>
        If you encounter an accessibility barrier — a control that&apos;s not keyboard-reachable, text that&apos;s hard to read, an error you can&apos;t dismiss — please tell us. Email <a href="mailto:accessibility@factory-os.com">accessibility@factory-os.com</a> and mention what you were trying to do, what happened, and your browser / OS / assistive technology if relevant. We treat these as priority bugs. More detail on the <a href="/accessibility">Accessibility Statement</a>.
      </p>

      <h2>Security</h2>
      <p>
        If you believe you have found a security issue, please email <a href="mailto:security@factory-os.com">security@factory-os.com</a> with details and steps to reproduce. Please give us a reasonable window to respond and fix before public disclosure.
      </p>

      <h2>Press and partnerships</h2>
      <p>
        Reach the team at <a href="mailto:press@factory-os.com">press@factory-os.com</a> (press) or <a href="mailto:partners@factory-os.com">partners@factory-os.com</a> (partnerships).
      </p>

      <div className="callout">
        <p style={{ margin: 0 }}>
          <strong>Heads up:</strong> Factory OS is under active development. Some features described on the marketing site may be in progress. The fastest way to learn what&apos;s live today is to <a href="https://demo.factory-os.com">take the 2-minute tour</a>.
        </p>
      </div>
    </PolicyLayout>
  );
};

export default ContactPage;
